/**
 * Group H — Discovery (`backend/api/v1/discovery.py`): the keyword audit of a community
 * the account holds, and the stream every audit is read through.
 *
 *   GET  /v1/communities/{id}/audit    the newest audit
 *   POST /v1/communities/{id}/audits   run it again
 *   GET  /v1/audits/{id}/stream        one audit's frames — live, or replayed whole
 *
 * An account still signing up holds no community; the audit its signup started is
 * `Onboarding.audit_id` (`./onboarding`), read through the same stream.
 */

import { api } from '../transport';
import { community } from './envelope';
import { apiGet, apiPost } from './http';

export interface AuditRef {
  id: number;
  status: 'running' | 'done' | 'failed';
  created_at: string | null;
}

/** Throws `CodedApiError` `audit_not_found` (404) when the community was never audited. */
export function fetchLatestAudit(skoolId: number): Promise<AuditRef> {
  return apiGet<AuditRef>(community(skoolId, '/audit'));
}

/**
 * Audit the community again. Answers with the run already going, if there is one.
 *
 * A community that sets no discovery keywords is audited too — it ranks for nothing, and
 * the report is then the recommendations alone. Throws `CodedApiError`
 * `service_unavailable` (503) while Skool rebuilds its index.
 *
 * `language` measures in one of Skool's discovery languages, or `all` for no filter.
 * Omit it and the community is measured in its OWN indexed language, which is what an
 * owner means by "where do I rank" — a German community among German ones.
 */
export function runAudit(skoolId: number, language?: string | null): Promise<AuditRef> {
  return apiPost<AuditRef>(community(skoolId, '/audits'),
    language ? { language } : undefined);
}

/** The audit's event stream, as a raw response — read it with `readFrames`. The frames
 *  are the public audit's, so `web-app/src/lib/audit.ts` reads both with one reader. */
export function openAuditStream(auditId: number): Promise<Response> {
  return api().raw(`/v1/audits/${auditId}/stream`);
}
